import { Alert, Button, Modal, Text } from '@mantine/core';
import type { AdPlatform } from '@marketing-tool/shared';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';

import { Table } from '../../../components/Table';
import * as remote from '../../../data/remote-data';
import { isLoading } from '../../../data/remote-data';
import { useAdInterpolation } from '../hooks';

export const AdInterpolator = (
    props: PropsWithChildren<{
        gameId: string;
        platform: AdPlatform;
        campaignId: string | number;
        adGroupId?: string | number;
    }>,
) => {
    const { gameId, platform, campaignId, adGroupId } = props;
    const { data, interpolate } = useAdInterpolation(gameId, platform, campaignId, adGroupId);
    const [isResultVisible, setResultVisible] = useState(false);
    const closeResults = () => setResultVisible(false);
    const onClick = async () => {
        await interpolate();
        setResultVisible(true);
    };

    return (
        <>
            <Button variant="subtle" disabled={isLoading(data)} onClick={onClick}>
                {props.children}
            </Button>
            {remote.matchSome(
                data,
                {
                    Success: (results) => {
                        const data = Object.entries(results).map(([name, url]) => ({
                            name,
                            url,
                        }));
                        return (
                            <Modal
                                opened={isResultVisible}
                                onClose={closeResults}
                                size="lg"
                                title="Interpolation Results"
                            >
                                {data.length === 0 ? (
                                    <p>No changes made</p>
                                ) : (
                                    <Table
                                        storageKey="ad-interpolator"
                                        id={(row) => row.name}
                                        data={data}
                                        columns={[
                                            {
                                                name: 'Ad',
                                                alt: 'Ad name',
                                                render: (row) => row.name,
                                            },
                                            {
                                                name: 'URL',
                                                alt: 'Landing page URL',
                                                render: (row) => row.url,
                                            },
                                        ]}
                                        defaultSortColumn="Ad"
                                    />
                                )}
                            </Modal>
                        );
                    },
                    Failure: (error) => (
                        <Alert color="red" title="Error loading ads">
                            <Text>{error}</Text>
                        </Alert>
                    ),
                },
                () => null,
            )}
        </>
    );
};
